import React, {
  forwardRef, useContext, useEffect, useMemo,
} from 'react';
import { TransitionProps } from '@mui/material/transitions';
import {
  Button,
  Dialog, DialogContent, DialogTitle, Grid, IconButton, Zoom,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { ObjectSchema } from 'yup';
import { TypesDialogBox } from '../../../../utils/interface';
import { LocalizationContext } from '../../../../Common/Localization/LocalizationProvider';
import { AddEquipmentCategoryListData, EquipmentCategoryList } from '../utils/types';
import ComponentTextInput from '../../../../Common/ComponentTextinput/ComponentTextInput';
import { EquipmentCategoryListFormValue } from '../utils/ConstantsEquipmentCategoryList';
import { RootState } from '../../../../redux/rootState';
import { equipmentCategoryListSchema } from '../utils/validations';
import {
  addEquipmentCategoryListStart, resetState, updateEquipmentCategoryList,
} from '../redux/sliceEquipmentCategoryList';
import RequiredFiled from '../../../../Common/RequiredField';

const Transition = forwardRef((
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) => <Zoom ref={ref} {...props} />);

function ComponentEquipmentCategoryListDialog({
  show, setOpenFrom, rowId, handleData,
}: TypesDialogBox): JSX.Element {
  const { translations } = useContext(LocalizationContext);
  const { isSuccess, EquipmentCategoryListData } = useSelector((state: RootState) => state.ListEquipmentCategoryReducer);

  const dispatch = useDispatch();

  const {
    formState: { errors }, control, handleSubmit, setValue, reset,
  } = useForm<AddEquipmentCategoryListData>({
    defaultValues: {
      Description: '',
    },
    resolver: yupResolver(equipmentCategoryListSchema as ObjectSchema<AddEquipmentCategoryListData>),
  });

  const onSubmit = (data: AddEquipmentCategoryListData) => {
    if (rowId) {
      const updateData = {
        Id: rowId,
        Description: data.Description,
      };
      dispatch(updateEquipmentCategoryList(updateData));
      reset();
      setOpenFrom();
    } else {
      const bodyData = {
        Description: data.Description,
      };
      dispatch(addEquipmentCategoryListStart(bodyData));
      reset();
      setOpenFrom();
    }
  };

  const tableData: EquipmentCategoryList[] = useMemo(() => EquipmentCategoryListData?.Data?.map((item, index) => {
    const transformedDataItem = {
      sn: index,
      action: 'edit&delete',
      Category: item.Description,
      id: String(item?.Id),
    };
    return transformedDataItem;
  }), [EquipmentCategoryListData]);

  useEffect(() => {
    if (rowId) {
      tableData?.forEach((rowData) => {
        if (rowData?.id === rowId) {
          setValue(EquipmentCategoryListFormValue.DESCRIPTION, rowData.Category);
        }
      });
    }
  }, [rowId, show]);

  useEffect(() => {
    if (isSuccess) {
      reset();
      if (handleData) {
        handleData();
      }
      dispatch(resetState());
    }
  }, [isSuccess]);

  const handleClose = () => {
    setOpenFrom();
    reset();
    setValue(EquipmentCategoryListFormValue.DESCRIPTION, '');
  };

  const getAnalystHeading = () => {
    if (rowId) {
      return 'Edit Equipment Category';
    }
    return 'Add Equipment Category';
  };

  return (
    <div>
      <Dialog
        maxWidth="sm"
        PaperProps={{
          className: 'paperStyle',
        }}
        open={show}
        transitionDuration={500}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle className="modalHeaderTitle">
          {getAnalystHeading()}
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent style={{ padding: '16px 24px' }}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Grid container spacing={2}>
              <Grid item md={12} sm={12} xs={12}>
                <div className="div_label_text">
                  <label className="label_Style_job">
                    Category
                    <RequiredFiled />
                  </label>
                  <ComponentTextInput
                    control={control}
                    className="nonCapitalizeField"
                    inputProps={{ maxLength: 50 }}
                    name={EquipmentCategoryListFormValue.DESCRIPTION}
                    size="small"
                    id={EquipmentCategoryListFormValue.DESCRIPTION}
                    errors={errors}
                  />
                </div>
              </Grid>
              <Grid item md={12} sm={12} xs={12}>
                <div className="save_and_next_div">
                  <div>
                    <Button className="button_cancel" onClick={handleClose}>{translations.cancel}</Button>
                  </div>
                  <div className="button_margin_left">
                    <Button type="submit" className="button_save_and_next">{translations.save}</Button>
                  </div>
                </div>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ComponentEquipmentCategoryListDialog;
